import React, { useState, useMemo, useRef } from 'react';
import { X, Receipt, CheckCircle2 } from 'lucide-react';
import { DialogShell } from './ui/DialogShell';
import { MoneyInput } from './ui/MoneyInput';
import { DatePicker } from './ui/DatePicker';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { deudaService } from '../services/deudaService';
import { DeudaConEstado } from '../lib/supabase';
import type { RespaldoMovimiento } from '../types/comprobante';
import { desglosarCuotas } from '../utils/deudaCalculations';
import { entidadPorColumnas } from '../utils/comprobanteEntidad';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import { formatCurrency } from '../utils/formatters';
import { fechaEnChile } from '../utils/dateHelpers';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface PagoDeudaModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly deuda: DeudaConEstado;
  /** 'pago' (default) = cuota/abono normal; 'saldar' = cierra el saldo restante de una vez. */
  readonly modo?: 'pago' | 'saldar';
}

export const PagoDeudaModal: React.FC<PagoDeudaModalProps> = ({ isOpen, onClose, onSuccess, deuda, modo = 'pago' }) => {
  const proximaCuota = useMemo(() => {
    const cuotas = desglosarCuotas(deuda, deuda.pagos);
    return cuotas.find(c => c.estado !== 'pagada');
  }, [deuda]);

  const numeroCuota = proximaCuota?.numeroCuota ?? deuda.numero_cuotas;
  const restanteCuota = proximaCuota ? proximaCuota.montoEsperado - proximaCuota.montoPagado : deuda.saldo;
  const esSaldar = modo === 'saldar';

  // Día civil de Chile (no el del reloj del equipo); vacío si no se puede determinar: se pide elegirlo.
  const [fecha, setFecha] = useState(() => fechaEnChile(new Date()) ?? '');
  const [monto, setMonto] = useState(restanteCuota);
  const [submitting, setSubmitting] = useState(false);

  const { error, clearError, handleError } = useErrorHandler();
  // "Registrar pago" y "Saldar" comparten el bloque: el pago que cierra la deuda se hace hoy.
  const comprobante = useComprobanteFormulario({
    entidad: entidadPorColumnas(deuda),
    fechaMovimiento: esSaldar ? fechaEnChile(new Date()) : fecha,
    permiteSubirDespues: true,
    pideMedioPago: true,
    sentidoDinero: 'egreso',
  });

  /** Valida el comprobante, lo sube una sola vez y ejecuta el pago con su respaldo. */
  const guardarConRespaldo = async (
    pagar: (respaldo: RespaldoMovimiento) => Promise<unknown>,
    mensajeInesperado: string,
  ) => {
    const errorComprobante = comprobante.validar();
    if (errorComprobante) { handleError(errorComprobante); return; }
    try {
      setSubmitting(true);
      const respaldo = await comprobante.resolver();
      await pagar(respaldo);
      comprobante.reiniciar();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, mensajeInesperado);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitPago = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (monto <= 0) { handleError('El monto debe ser mayor a cero'); return; }
    if (monto > restanteCuota) { handleError('El monto excede lo que falta de esta cuota'); return; }
    if (!fecha) { handleError('Indica la fecha del pago'); return; }
    if (fecha < deuda.fecha_inicio_credito) { handleError('La fecha del pago no puede ser anterior al inicio de la deuda'); return; }

    await guardarConRespaldo(
      (respaldo) => deudaService.registrarPago(deuda.id, numeroCuota, fecha, monto, respaldo),
      'Error al registrar el pago. Por favor intenta nuevamente.',
    );
  };

  const handleSaldar = async () => {
    clearError();
    await guardarConRespaldo(
      (respaldo) => deudaService.saldar(deuda, respaldo),
      'Error al saldar la deuda. Por favor intenta nuevamente.',
    );
  };

  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();
  // Al cerrar sin guardar se sueltan las vistas previas del comprobante elegido.
  const cerrar = () => { comprobante.reiniciar(); onClose(); };

  if (!isOpen) return null;

  const bloqueado = submitting || comprobante.subiendo;

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={cerrar}
      size="sm"
      level="nested"
      hasUnsavedChanges={submitting || comprobante.hayCambios}
      closeRef={closeRef}
      header={(
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`${esSaldar ? 'bg-emerald-100' : 'bg-blue-100'} p-2 rounded-lg`}>
              {esSaldar ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <Receipt className="w-6 h-6 text-blue-600" />}
            </div>
            <h2 className="text-2xl font-bold text-slate-800">{esSaldar ? 'Saldar deuda' : 'Registrar pago'}</h2>
          </div>
          <button type="button" onClick={requestClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" aria-label="Cerrar">
            <X className="w-6 h-6 text-slate-600" />
          </button>
        </div>
      )}
    >
      <FormErrorAlert error={error} onClear={clearError} spacingClassName="mb-4" />

      {esSaldar ? (
        <div className="space-y-6">
          <ComprobanteInput comprobante={comprobante} deshabilitado={submitting} />
          <p className="text-slate-700">
            Se registrará un pago único de <span className="font-semibold">{formatCurrency(deuda.saldo)}</span> que
            cierra el saldo restante de la deuda con <span className="font-semibold">{deuda.acreedor}</span>.
          </p>
          <MedioPagoFolio comprobante={comprobante} deshabilitado={submitting} />
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={requestClose}
              className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaldar}
              className="flex-1 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={bloqueado}
            >
              {submitting ? 'Guardando...' : 'Saldar deuda'}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmitPago} className="space-y-6">
          <ComprobanteInput comprobante={comprobante} deshabilitado={submitting} />

          <p className="text-sm text-slate-500">
            Cuota {numeroCuota} de {deuda.numero_cuotas} — saldo de esta cuota: {formatCurrency(restanteCuota)}
          </p>

          <MoneyInput label="Monto del pago" value={monto} onChange={setMonto} required />
          <DatePicker label="Fecha del pago" value={fecha} onChange={setFecha} min={deuda.fecha_inicio_credito} required />
          <MedioPagoFolio comprobante={comprobante} deshabilitado={submitting} />

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={requestClose}
              className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={bloqueado}
            >
              {submitting ? 'Guardando...' : 'Registrar pago'}
            </button>
          </div>
        </form>
      )}
    </DialogShell>
  );
};
